import { isNativePlatform, isRealNativePlatform } from "./platform";

/**
 * One-time native runtime setup, called from src/index.tsx before React mounts.
 *
 * UI-level setup (root class, viewport-fit) runs whenever {@link isNativePlatform}
 * is true — including `VITE_FORCE_NATIVE` browser dev, where it is harmless
 * (browsers report zero safe-area insets). Plugin-backed behavior (StatusBar,
 * external-link shim) only runs in the real container.
 */
export function bootstrapNative(): void {
  if (!isNativePlatform()) return;

  document.documentElement.classList.add("cohesive-native");
  ensureViewportFitCover();

  if (isRealNativePlatform()) {
    void syncStatusBarWithTheme();
    installExternalLinkShim();
  }
}

/**
 * Ensure the viewport meta opts into the safe-area (`env(safe-area-inset-*)`)
 * model. We only *add* `viewport-fit=cover`; the existing `maximum-scale=1` is
 * kept because it stops iOS from zooming when an input is focused.
 */
function ensureViewportFitCover(): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (!meta) return;
  if (/viewport-fit\s*=/.test(meta.content)) return;
  meta.content = `${meta.content.replace(/\s*$/, "")}, viewport-fit=cover`;
}

/**
 * Keep the iOS status-bar text legible against the app background by following
 * the `dark` class the theme provider toggles on <html>.
 */
async function syncStatusBarWithTheme(): Promise<void> {
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    const apply = () => {
      const isDark = document.documentElement.classList.contains("dark");
      // Style.Dark = light text (for dark backgrounds); Style.Light = dark text.
      void StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
    };
    apply();
    new MutationObserver(apply).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
  } catch {
    /* status-bar plugin unavailable — ignore */
  }
}

/**
 * Route external http(s) links to the system browser sheet instead of letting
 * them replace the app's webview (which would strand the user outside the app).
 * Same-origin / relative links keep flowing through react-router normally.
 */
function installExternalLinkShim(): void {
  const openExternal = async (url: string): Promise<void> => {
    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url });
    } catch {
      /* ignore */
    }
  };

  const isExternalHttp = (url: string): boolean =>
    /^https?:\/\//i.test(url) && !url.startsWith(window.location.origin);

  const nativeOpen = window.open.bind(window);
  window.open = ((
    url?: string | URL,
    target?: string,
    features?: string,
  ): Window | null => {
    const href = typeof url === "string" ? url : (url?.toString() ?? "");
    if (href && isExternalHttp(href)) {
      void openExternal(href);
      return null;
    }
    return nativeOpen(url as string | URL, target, features);
  }) as typeof window.open;

  document.addEventListener(
    "click",
    (event) => {
      const anchor = (event.target as HTMLElement | null)?.closest?.("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href") ?? "";
      if (isExternalHttp(href)) {
        event.preventDefault();
        void openExternal(href);
      }
    },
    true,
  );
}
